// @flow
import React from 'react';
import { bindActionCreators, compose } from 'redux';
import { capitalize, isEqual } from 'lodash';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';

import Button from 'components/Button.jsx';
import Glyph from 'components/Glyph.jsx';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupChatAvatar from 'chat/components/GroupChatAvatar.jsx';
import MediaObject from 'components/MediaObject.jsx';
import OrgAvatar from 'organizations/components/OrgAvatar.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import isNumber from 'lodash/isNumber';
import UserAvatar from 'users/components/UserAvatar.jsx';

import {
  RECIPIENT_TYPES,
  selectRecipient,
  updateTotalRecipientCount,
} from 'reducers/roleBasedMessagingSearch';
import type {
  Recipient,
  RecipientCount,
  MessageFilterType,
} from 'reducers/roleBasedMessagingSearch';

type Props = {
  selection: Recipient,
  data: ReactApolloData<RecipientSelectionItemQuery>,

  // redux
  onSelectRecipient: (recipient: Recipient, filter: ?MessageFilterType) => *,
  updateTotalRecipientCount: (recipientCount: RecipientCount) => *,
};

type State = {
  hovered: boolean,
  count: number,
};

class RecipientSelectionItem extends React.Component<Props, State> {
  state = {
    hovered: false,
    count: 0,
  };

  componentDidMount() {
    const { data, selection } = this.props;
    if (data && data.countRecipients) {
      this.updateCount(data.countRecipients.count);
      return;
    }
    if (selection.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
      const { chat } = selection;
      this.updateCount(chat.getMembershipsCount() - 1);
      return;
    }
    if (selection.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
      this.updateCount(1);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.data || !prevProps.data) {
      return;
    }
    const currentCountRecipients = this.props.data.countRecipients;
    if (!isEqual(prevProps.data.countRecipients, currentCountRecipients)) {
      this.updateCount(currentCountRecipients.count);
    }
  }

  componentWillUnmount() {
    this.updateCount(0);
  }

  updateCount(count: number) {
    this.setState({ count });
    this.props.updateTotalRecipientCount({ id: this.props.selection.id, count });
  }

  handleRemoval = () => {
    const { selection } = this.props;
    if (!selection) {
      return null;
    }
    return this.props.onSelectRecipient(selection, null);
  };

  renderSelectionItem = (options: {
    avatar: *,
    primaryText: string,
    secondaryText?: string,
    count?: number,
    eventKey: string,
    eventProperties?: Object,
  }) => {
    const { avatar, primaryText, secondaryText, count, eventKey, eventProperties } = options;
    return (
      <li
        className="SelectFromList-selectionItem"
        onMouseOver={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}>
        <Row size="small">
          <MediaObject figure="right" gutter="xsmall" center={!secondaryText}>
            <MediaObject gutter="small" center={!secondaryText}>
              {avatar}
              <div>
                <Text size="xsmall" element="div" className="SelectFromList-selectionItem-name">
                  {primaryText}
                </Text>
                {secondaryText && (
                  <Text size="xxsmall" element="div" color="eclipse">
                    {secondaryText}
                  </Text>
                )}
              </div>
            </MediaObject>
            <div className="SelectFromList-selectionItem-figure">
              {this.state.hovered ? (
                <Button
                  aria-label={`Remove ${primaryText} from selection`}
                  onClick={this.handleRemoval}
                  level="icon"
                  data-test="close-btn"
                  eventKey={eventKey}
                  eventProperties={eventProperties}>
                  <Glyph name="close-circle" color="eclipse" size="medium" solo />
                </Button>
              ) : (
                <Text size="xsmall" color="eclipse">
                  {isNumber(count) ? count : ''}
                </Text>
              )}
            </div>
          </MediaObject>
        </Row>
      </li>
    );
  };

  render() {
    const { selection } = this.props;
    const { count } = this.state;
    if (!selection) {
      return null;
    }
    if (selection.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
      const { organization } = selection;
      const filterValues = selection.selectedFilters
        ? selection.selectedFilters.map(filter => filter.value.label)
        : [];
      return this.renderSelectionItem({
        avatar: <OrgAvatar size="xsmall" />,
        primaryText: organization.name,
        secondaryText: filterValues.join(', '),
        count,
        eventKey: 'announcements.share_to.organization.selected_list.unselect.click',
        eventProperties: { organization_uuid: organization.uuid },
      });
    }
    if (selection.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
      const { group } = selection;
      const filterValues = selection.selectedFilters
        ? selection.selectedFilters.map(filter => filter.value.label)
        : [];
      return this.renderSelectionItem({
        avatar: <GroupAvatar group={group} size="xsmall" />,
        primaryText: group.className,
        secondaryText: filterValues.join(', '),
        count,
        eventKey: 'announcements.share_to.group.selected_list.unselect.click',
      });
    }
    if (selection.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
      const { chat } = selection;
      return this.renderSelectionItem({
        avatar: <GroupChatAvatar size="xsmall" />,
        primaryText: chat.getTitle(),
        secondaryText: `${count} people`,
        count,
        eventKey: 'announcements.share_to.group_chat.selected_list.unselect.click',
      });
    }
    if (selection.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
      const { member } = selection;
      return this.renderSelectionItem({
        avatar: <UserAvatar user={member.getUser().toJSON()} size="xsmall" hideBadge />,
        primaryText: member.getName(),
        secondaryText: capitalize(member.getRole()),
        count,
        eventKey: 'announcements.share_to.individual.selected_list.unselect.click',
      });
    }
    return null;
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  // $FlowIssue
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
      updateTotalRecipientCount,
    },
    dispatch
  );
};

const withCount = graphql(
  gql`
    query RecipientSelectionItem($input: RecipientInput!) {
      countRecipients(input: $input) {
        count
      }
    }
  `,
  {
    skip: ({ selection }) =>
      selection.type !== RECIPIENT_TYPES.ORGANIZATION_RECIPIENT &&
      selection.type !== RECIPIENT_TYPES.GROUP_RECIPIENT,
    options: ({ selection }) => {
      const isOrg = selection.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT;
      const type = isOrg ? 'organization' : 'group';
      const uuid = isOrg ? selection.organization.uuid : selection.group.uuid;
      const roles = selection.selectedFilters
        ? selection.selectedFilters
            .filter(filter => filter.type === 'roles')
            .map(filter => filter.value.id)
        : [];
      const filters = roles.length ? [{ roles }] : [];
      return {
        variables: { input: { type, uuid, filters } },
      };
    },
  }
);

export { RecipientSelectionItem };

export default compose(
  connect(
    undefined,
    mapDispatchToProps
  ),
  withCount
)(RecipientSelectionItem);
